/**
 * Landing-page presentation of the plans (copy, features, CTAs).
 *
 * The PRICES are not defined here: since Step 14 they come from
 * `BILLING_PLANS` in @platform/shared, the same table the API uses for
 * Checkout and the admin MRR estimate. Change a price there and the
 * landing page, the billing page and the admin numbers all follow. Nothing
 * in `routes/public/home/*` hardcodes a price -- every component reads
 * from this file. (The landing page calls the `pro` plan "Growth"; that
 * name also comes from BILLING_PLANS.)
 */
import { BILLING_PLANS } from "@platform/shared";

export interface PricingPlan {
  id: "starter" | "growth" | "custom";
  name: string;
  /** Monthly price in whole USD, or null for "custom" / "talk to us" plans. */
  monthlyPrice: number | null;
  priceSuffix: string;
  description: string;
  features: string[];
  ctaLabel: string;
  ctaHref: string;
  highlighted?: boolean;
}

export const PRICING_CURRENCY_SYMBOL = "$";

export const PRICING_PLANS: PricingPlan[] = [
  {
    id: "starter",
    name: BILLING_PLANS.starter.name,
    monthlyPrice: BILLING_PLANS.starter.monthlyPriceCents / 100,
    priceSuffix: "/month",
    description: "For a single location that wants every missed call followed up automatically.",
    features: [
      "1 business phone line",
      "Automatic missed-call SMS",
      "Lead dashboard",
      "Conversation history",
      "Email notifications",
    ],
    ctaLabel: "Start free trial",
    ctaHref: "/signup",
  },
  {
    id: "growth",
    name: BILLING_PLANS.pro.name,
    monthlyPrice: BILLING_PLANS.pro.monthlyPriceCents / 100,
    priceSuffix: "/month",
    description: "For teams that want AI qualification and custom rules across more lines.",
    features: [
      "Up to 5 business phone lines",
      "Everything in Starter",
      "AI lead qualification",
      "Custom qualification rules",
      "Analytics & reporting",
      "Priority support",
    ],
    ctaLabel: "Start free trial",
    ctaHref: "/signup",
    highlighted: true,
  },
  {
    id: "custom",
    name: "Custom",
    monthlyPrice: null,
    priceSuffix: "",
    description: "For multi-location or franchise businesses with specific requirements.",
    features: [
      "Unlimited phone lines",
      "Everything in Growth",
      "Custom integrations",
      "Dedicated onboarding",
      "Volume pricing",
    ],
    ctaLabel: "Talk to us",
    ctaHref: "#contact",
  },
];

export function formatMonthlyPrice(plan: PricingPlan): string {
  if (plan.monthlyPrice === null) return "Custom";
  return `${PRICING_CURRENCY_SYMBOL}${plan.monthlyPrice}`;
}
